import React, { useState, useMemo, memo, useCallback } from 'react';
import { FilterItem } from './components/FilterItem';
import { FilterChips } from './components/FilterChips';
import type {
  FilterPanelProps,
  FilterValue,
  FilterChip,
  DateRangeValue,
  NumberRangeValue
} from './FilterPanel.types';

// Icons
const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M4 6l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
    <path d="M2 3h12M4 7h8M6 11h4" strokeLinecap="round" />
  </svg>
);

const formatDateValue = (date: Date | null): string => {
  if (!date) return '';
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

function FilterPanelComponent({
  filters,
  values,
  onChange,
  onClear,
  collapsible = false,
  defaultCollapsed = false,
  className,
  showChips = true,
  orientation = 'horizontal',
  compact = false
}: FilterPanelProps): React.ReactElement {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  // Handle value change for a single filter
  const handleFilterChange = useCallback((filterId: string, value: FilterValue) => {
    onChange({
      ...values,
      [filterId]: value
    });
  }, [values, onChange]);

  // Remove a single filter
  const handleRemoveFilter = useCallback((filterId: string) => {
    const newValues = { ...values };
    delete newValues[filterId];
    onChange(newValues);
  }, [values, onChange]);

  // Clear all filters
  const handleClearAll = useCallback(() => {
    if (onClear) {
      onClear();
    } else {
      onChange({});
    }
  }, [onChange, onClear]);

  // Generate chips from active filters
  const chips = useMemo((): FilterChip[] => {
    const result: FilterChip[] = [];

    filters.forEach(filter => {
      const value = values[filter.id];
      if (value === null || value === undefined) return;

      let displayValue = '';

      switch (filter.type) {
        case 'select': {
          if (typeof value === 'string') {
            const option = filter.options?.find(opt => opt.value === value);
            displayValue = option?.label || value;
          }
          break;
        }

        case 'multiselect': {
          if (Array.isArray(value) && value.length > 0) {
            if (value.length === 1) {
              const option = filter.options?.find(opt => opt.value === value[0]);
              displayValue = option?.label || value[0];
            } else {
              displayValue = `${value.length} seçili`;
            }
          } else {
            return; // Skip empty arrays
          }
          break;
        }

        case 'search': {
          if (typeof value === 'string' && value.trim()) {
            displayValue = `"${value}"`;
          } else {
            return; // Skip empty strings
          }
          break;
        }

        case 'dateRange': {
          const dateValue = value as DateRangeValue;
          if (dateValue.start || dateValue.end) {
            const startStr = formatDateValue(dateValue.start);
            const endStr = formatDateValue(dateValue.end);
            if (startStr && endStr) {
              displayValue = `${startStr} - ${endStr}`;
            } else if (startStr) {
              displayValue = `${startStr}'den itibaren`;
            } else if (endStr) {
              displayValue = `${endStr}'e kadar`;
            }
          } else {
            return;
          }
          break;
        }

        case 'numberRange': {
          const numValue = value as NumberRangeValue;
          if (numValue.min !== null || numValue.max !== null) {
            if (numValue.min !== null && numValue.max !== null) {
              displayValue = `${numValue.min} - ${numValue.max}`;
            } else if (numValue.min !== null) {
              displayValue = `≥ ${numValue.min}`;
            } else if (numValue.max !== null) {
              displayValue = `≤ ${numValue.max}`;
            }
          } else {
            return;
          }
          break;
        }

        case 'boolean': {
          if (typeof value === 'boolean') {
            displayValue = value ? 'Evet' : 'Hayır';
          } else {
            return;
          }
          break;
        }
      }

      if (displayValue) {
        result.push({
          filterId: filter.id,
          label: filter.label,
          displayValue
        });
      }
    });

    return result;
  }, [filters, values]);

  const activeFilterCount = chips.length;

  const isVertical = orientation === 'vertical';

  return (
    <div
      className={className}
      style={{
        background: 'var(--color-bg-card)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-border-default)',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      {collapsible && (
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          aria-expanded={!isCollapsed}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            padding: compact ? 'var(--space-3)' : 'var(--space-4)',
            background: 'var(--color-bg-secondary)',
            border: 'none',
            borderBottom: isCollapsed ? 'none' : '1px solid var(--color-border-subtle)',
            cursor: 'pointer',
            transition: 'background var(--transition-fast)'
          }}
        >
          <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            fontSize: 'var(--text-sm)',
            fontWeight: 600,
            color: 'var(--color-text-primary)'
          }}>
            <FilterIcon />
            Filtreler
            {activeFilterCount > 0 && (
              <span style={{
                padding: '0 var(--space-2)',
                fontSize: 'var(--text-xs)',
                fontWeight: 600,
                background: 'var(--color-primary-100)',
                color: 'var(--color-primary-700)',
                borderRadius: 'var(--radius-full)'
              }}>
                {activeFilterCount}
              </span>
            )}
          </span>
          <span style={{
            transform: isCollapsed ? 'rotate(0)' : 'rotate(180deg)',
            transition: 'transform var(--transition-fast)',
            color: 'var(--color-text-tertiary)'
          }}>
            <ChevronDownIcon />
          </span>
        </button>
      )}

      {/* Content */}
      {!isCollapsed && (
        <div style={{
          padding: compact ? 'var(--space-3)' : 'var(--space-4)'
        }}>
          {/* Filter Items */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isVertical ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: compact ? 'var(--space-3)' : 'var(--space-4)'
          }}>
            {filters.map(filter => (
              <FilterItem
                key={filter.id}
                config={filter}
                value={values[filter.id]}
                onChange={(value) => handleFilterChange(filter.id, value)}
                compact={compact}
              />
            ))}
          </div>

          {/* Active Filter Chips */}
          {showChips && chips.length > 0 && (
            <div style={{
              marginTop: compact ? 'var(--space-3)' : 'var(--space-4)',
              paddingTop: compact ? 'var(--space-3)' : 'var(--space-4)',
              borderTop: '1px solid var(--color-border-subtle)'
            }}>
              <FilterChips
                chips={chips}
                onRemove={handleRemoveFilter}
                onClearAll={handleClearAll}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const FilterPanel = memo(FilterPanelComponent);
